import React, { useState, useEffect } from 'react';
import { getSystemConfig, updateSystemConfig, SystemConfig } from '@/shared/services/systemConfigService';
import styles from './SystemConfigPage.module.css';

export const SystemConfigPage: React.FC = () => {
    const [config, setConfig] = useState<SystemConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Form states
    const [platformFee, setPlatformFee] = useState<number>(15);
    const [deliveryBaseFee, setDeliveryBaseFee] = useState<number>(15);
    const [minOrder, setMinOrder] = useState<number>(0);
    // State for Escalation
    const [reNotifyMinutes, setReNotifyMinutes] = useState<number>(5);
    const [alertAdminMinutes, setAlertAdminMinutes] = useState<number>(10);
    const [autoCancelMinutes, setAutoCancelMinutes] = useState<number>(30);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await getSystemConfig();
            setConfig(data);
            setPlatformFee(data.platformFeePercent);
            setDeliveryBaseFee(data.deliveryBaseFee);
            setMinOrder(data.minimumOrderGlobal);

            // Cargar escalamiento (con defaults si no existen)
            if (data.orderEscalation) {
                setReNotifyMinutes(data.orderEscalation.reNotifyMinutes);
                setAlertAdminMinutes(data.orderEscalation.alertAdminMinutes);
                setAutoCancelMinutes(data.orderEscalation.autoCancelMinutes);
            }
        } catch (err) {
            console.error('Error loading config:', err);
            setError('No se pudo cargar la configuración del sistema.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await updateSystemConfig({
                platformFeePercent: platformFee,
                deliveryBaseFee: deliveryBaseFee,
                minimumOrderGlobal: minOrder,
                orderEscalation: {
                    reNotifyMinutes,
                    alertAdminMinutes,
                    autoCancelMinutes
                }
            });
            setSuccess('Configuración actualizada correctamente.');
            // Actualizar el timestamp local
            loadConfig();
        } catch (err) {
            console.error('Error saving config:', err);
            setError('Error al guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className={styles.loading}>Cargando panel maestro...</div>;

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1>Ajustes del Sistema</h1>
                <p>Configuración global y financiera de la plataforma.</p>
            </header>

            {error && <div className={styles.errorAlert}>{error}</div>}
            {success && <div className={styles.successAlert}>{success}</div>}

            <form onSubmit={handleSave} className={styles.card}>
                <div className={styles.section}>
                    <h3>💰 Tarifas y Comisiones</h3>
                    <div className={styles.grid}>
                        <div className={styles.inputGroup}>
                            <label>Comisión de Plataforma (%)</label>
                            <div className={styles.inputWithUnit}>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={platformFee}
                                    onChange={(e) => setPlatformFee(Number(e.target.value))}
                                    required
                                />
                                <span className={styles.unit}>%</span>
                            </div>
                            <small>Este porcentaje se cobra al subtotal de productos de cada restaurante.</small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Costo de Envío Base (BOB)</label>
                            <div className={styles.inputWithUnitLeft}>
                                <span className={styles.unitLeft}>Bs.</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={deliveryBaseFee}
                                    onChange={(e) => setDeliveryBaseFee(Number(e.target.value))}
                                    required
                                />
                            </div>
                            <small>Monto estándar sugerido para los servicios de entrega.</small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Pedido Mínimo Global (BOB)</label>
                            <div className={styles.inputWithUnitLeft}>
                                <span className={styles.unitLeft}>Bs.</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={minOrder}
                                    onChange={(e) => setMinOrder(Number(e.target.value))}
                                    required
                                />
                            </div>
                            <small>Gasto mínimo para que el cliente pueda realizar un pedido.</small>
                        </div>
                    </div>
                </div>

                <div className={styles.divider} style={{ margin: '2rem 0', borderTop: '1px solid #eee' }}></div>

                <div className={styles.section}>
                    <h3>⏱️ Tiempos de Escalamiento Automático</h3>
                    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                        Reglas para pedidos que se quedan "Buscando repartidor".
                    </p>
                    <div className={styles.grid}>
                        <div className={styles.inputGroup}>
                            <label>Re-notificar Repartidores (min)</label>
                            <div className={styles.inputWithUnit}>
                                <input
                                    type="number"
                                    min="1"
                                    value={reNotifyMinutes}
                                    onChange={(e) => setReNotifyMinutes(Number(e.target.value))}
                                    required
                                />
                                <span className={styles.unit}>min</span>
                            </div>
                            <small>Tiempo para enviar notificación urgente a repartidores.</small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Alerta a Administración (min)</label>
                            <div className={styles.inputWithUnit}>
                                <input
                                    type="number"
                                    min="1"
                                    value={alertAdminMinutes}
                                    onChange={(e) => setAlertAdminMinutes(Number(e.target.value))}
                                    required
                                />
                                <span className={styles.unit}>min</span>
                            </div>
                            <small>Tiempo para generar una incidencia automática.</small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Auto-Cancelación (min)</label>
                            <div className={styles.inputWithUnit}>
                                <input
                                    type="number"
                                    min="1"
                                    value={autoCancelMinutes}
                                    onChange={(e) => setAutoCancelMinutes(Number(e.target.value))}
                                    required
                                />
                                <span className={styles.unit}>min</span>
                            </div>
                            <small>Tiempo máximo antes de cancelar el pedido automáticamente.</small>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    {config && (
                        <div className={styles.lastUpdate}>
                            Última actualización: {(() => {
                                if (!config.updatedAt) return 'Nunca';
                                const date = config.updatedAt.seconds
                                    ? new Date(config.updatedAt.seconds * 1000)
                                    : new Date(config.updatedAt as any);
                                return isNaN(date.getTime()) ? 'Pendiente' : date.toLocaleString();
                            })()}
                        </div>
                    )}
                    <button type="submit" className={styles.saveButton} disabled={saving}>
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </form>

            <div className={styles.infoBox}>
                <h4>📌 Nota de Seguridad</h4>
                <p>Los cambios realizados en esta sección se aplican **al instante** para todos los nuevos pedidos. Los pedidos que ya están en curso no se verán afectados por el cambio de comisión.</p>
            </div>
        </div>
    );
};
